import { ClientTypeType } from '../digital-audit-constants';
import { EvidenceBlob } from './martech-scanner-constants';
import {
  CANONICAL_EVENTS,
  CanonicalEvent,
  EVENT_CANON,
  FireSource,
  KEY_EVENTS,
  KEY_EVENT_TYPES,
  PARAM_CANON,
  PRIMARY_CONVERSION_EVENTS,
  TagFire,
  WIRE_EVENT_PARAMS,
  WIRE_ID_PARAMS,
  WIRE_SIGNATURES
} from './tag-fire-constants';

const MAX_PARAM_VALUE = 120;
const MAX_RAW = 300;
const MAX_FIRES_PER_BLOB = 400; // runaway-page guard

/** One-level-nested JS object literal (bounded, non-catastrophic). */
const OBJ = String.raw`\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\}`;

const clean = (value: string): string =>
  value
    .replace(/^['"]|['"]$/g, '')
    .trim()
    .slice(0, MAX_PARAM_VALUE);

/** Shallow key/value extraction from a JS object-literal source string. */
const parseObjectParams = (objText: string | undefined): Record<string, string> => {
  const params: Record<string, string> = {};
  if (!objText) return params;
  const pair = /['"]?([A-Za-z_$][\w.$]*)['"]?\s*:\s*('[^']*'|"[^"]*"|\[[^\]]*\]|[\w.+-]+)/g;
  let m = pair.exec(objText);
  while (m) {
    params[m[1]] = clean(m[2]);
    m = pair.exec(objText);
  }
  return params;
};

const canonicalize = (platform: string, event: string): CanonicalEvent | null =>
  EVENT_CANON[platform]?.[event.toLowerCase().trim()] ?? null;

const makeFire = (
  platform: string,
  source: TagFire['source'],
  fields: Partial<Omit<TagFire, 'platform' | 'source' | 'canonicalEvent'>> & { event: string }
): TagFire => ({
  platform,
  source,
  id: fields.id ?? '',
  event: fields.event,
  canonicalEvent: canonicalize(platform, fields.event),
  params: fields.params ?? {},
  trigger: fields.trigger ?? 'scripted call',
  origin: fields.origin ?? '',
  raw: (fields.raw ?? '').slice(0, MAX_RAW)
});

/** Resolve which Google product a gtag destination id belongs to. */
const gtagPlatform = (id: string): string => {
  if (id.startsWith('G-')) return 'Google Analytics 4';
  if (id.startsWith('AW-')) return 'Google Ads Conversion';
  if (id.startsWith('DC-')) return 'Floodlight / DV360 / CM360';
  if (id.startsWith('UA-')) return 'Universal Analytics (legacy)';
  return 'Google Tag (gtag.js)';
};

interface CodeExtractor {
  pattern: RegExp;
  toFires: (m: RegExpExecArray, origin: string) => TagFire[];
}

const CODE_EXTRACTORS: CodeExtractor[] = [
  {
    // gtag('config'|'event'|'consent', 'x', {…}?)
    pattern: new RegExp(
      String.raw`gtag\(\s*['"](config|event|consent)['"]\s*,\s*['"]([^'"]+)['"](?:\s*,\s*(${OBJ}))?`,
      'g'
    ),
    toFires: (m, origin) => {
      const [raw, kind, name, obj] = m;
      const params = parseObjectParams(obj);
      if (kind === 'consent') {
        return [
          makeFire('Google Tag (gtag.js)', FireSource.CODE, {
            event: `consent ${name}`,
            params,
            trigger: 'page load',
            origin,
            raw
          })
        ];
      }
      const sendTo = params.send_to ?? '';
      const id = kind === 'config' ? name : sendTo;
      const platform = gtagPlatform(id || name);
      const event = kind === 'config' ? 'config' : name;
      return [
        makeFire(platform, FireSource.CODE, {
          id: id.split('/')[0],
          event: platform === 'Google Ads Conversion' && event !== 'config' ? 'conversion' : event,
          params,
          trigger: kind === 'config' ? 'page load' : 'scripted event call',
          origin,
          raw
        })
      ];
    }
  },
  {
    // dataLayer.push({event: 'x', …})
    pattern: new RegExp(String.raw`dataLayer\.push\(\s*(${OBJ})\s*\)`, 'g'),
    toFires: (m, origin) => {
      const params = parseObjectParams(m[1]);
      if (!params.event || params.event.startsWith('gtm.')) return [];
      const { event, ...rest } = params;
      return [
        makeFire('GTM dataLayer', FireSource.CODE, {
          event,
          params: rest,
          trigger: 'dataLayer event',
          origin,
          raw: m[0]
        })
      ];
    }
  },
  {
    // fbq('init'|'track'|'trackCustom', 'x', {…}?)
    pattern: new RegExp(
      String.raw`fbq\(\s*['"](init|track|trackCustom)['"]\s*,\s*['"]([^'"]+)['"](?:\s*,\s*(${OBJ}))?`,
      'g'
    ),
    toFires: (m, origin) => [
      makeFire('Meta (Facebook) Pixel', FireSource.CODE, {
        id: m[1] === 'init' ? m[2] : '',
        event: m[1] === 'init' ? 'init' : m[2],
        params: parseObjectParams(m[3]),
        trigger: m[1] === 'init' ? 'page load' : 'scripted event call',
        origin,
        raw: m[0]
      })
    ]
  },
  {
    // ttq.track('x', {…}?) / ttq.page()
    pattern: new RegExp(
      String.raw`ttq\.(track|page|identify)\(\s*(?:['"]([^'"]+)['"])?(?:\s*,?\s*(${OBJ}))?`,
      'g'
    ),
    toFires: (m, origin) =>
      m[1] === 'identify'
        ? []
        : [
            makeFire('TikTok Pixel', FireSource.CODE, {
              event: m[1] === 'page' ? 'page' : (m[2] ?? ''),
              params: parseObjectParams(m[3]),
              trigger: m[1] === 'page' ? 'page load' : 'scripted event call',
              origin,
              raw: m[0]
            })
          ]
  },
  {
    // pintrk('load'|'track'|'page', 'x'?, {…}?)
    pattern: new RegExp(
      String.raw`pintrk\(\s*['"](load|track|page)['"](?:\s*,\s*['"]([^'"]+)['"])?(?:\s*,\s*(${OBJ}))?`,
      'g'
    ),
    toFires: (m, origin) => {
      const pintrkEvent = (): string => {
        if (m[1] === 'load') return 'init';
        if (m[1] === 'page') return 'pagevisit';
        return m[2] ?? '';
      };
      return [
        makeFire('Pinterest Tag', FireSource.CODE, {
          id: m[1] === 'load' ? (m[2] ?? '') : '',
          event: pintrkEvent(),
          params: parseObjectParams(m[3]),
          trigger: m[1] === 'track' ? 'scripted event call' : 'page load',
          origin,
          raw: m[0]
        })
      ];
    }
  },
  {
    // snaptr('init'|'track', 'x', {…}?)
    pattern: new RegExp(
      String.raw`snaptr\(\s*['"](init|track)['"]\s*,\s*['"]([^'"]+)['"](?:\s*,\s*(${OBJ}))?`,
      'g'
    ),
    toFires: (m, origin) => [
      makeFire('Snap Pixel', FireSource.CODE, {
        id: m[1] === 'init' ? m[2] : '',
        event: m[1] === 'init' ? 'init' : m[2],
        params: parseObjectParams(m[3]),
        trigger: m[1] === 'init' ? 'page load' : 'scripted event call',
        origin,
        raw: m[0]
      })
    ]
  },
  {
    // uetq.push('event', 'x', {…})
    pattern: new RegExp(
      String.raw`uetq\.push\(\s*['"]event['"]\s*,\s*['"]([^'"]*)['"]\s*,\s*(${OBJ})`,
      'g'
    ),
    toFires: (m, origin) => [
      makeFire('Microsoft UET', FireSource.CODE, {
        event: m[1] || 'page_view',
        params: parseObjectParams(m[2]),
        trigger: 'scripted event call',
        origin,
        raw: m[0]
      })
    ]
  },
  {
    // rdt('track', 'x', {…}?)
    pattern: new RegExp(
      String.raw`rdt\(\s*['"]track['"]\s*,\s*['"]([^'"]+)['"](?:\s*,\s*(${OBJ}))?`,
      'g'
    ),
    toFires: (m, origin) => [
      makeFire('Reddit Pixel', FireSource.CODE, {
        event: m[1],
        params: parseObjectParams(m[2]),
        trigger: 'scripted event call',
        origin,
        raw: m[0]
      })
    ]
  },
  {
    // twq('event', 'tw-…', {…}?)
    pattern: new RegExp(
      String.raw`twq\(\s*['"](?:event|track)['"]\s*,\s*['"]([^'"]+)['"](?:\s*,\s*(${OBJ}))?`,
      'g'
    ),
    toFires: (m, origin) => [
      makeFire('X / Twitter Pixel', FireSource.CODE, {
        event: m[1],
        params: parseObjectParams(m[2]),
        trigger: 'scripted event call',
        origin,
        raw: m[0]
      })
    ]
  },
  {
    // _learnq.push(['track', 'X', {…}?])
    pattern: new RegExp(
      String.raw`_learnq\.push\(\s*\[\s*['"]track['"]\s*,\s*['"]([^'"]+)['"](?:\s*,\s*(${OBJ}))?`,
      'g'
    ),
    toFires: (m, origin) => [
      makeFire('Klaviyo', FireSource.CODE, {
        event: m[1],
        params: parseObjectParams(m[2]),
        trigger: 'scripted event call',
        origin,
        raw: m[0]
      })
    ]
  }
];

/**
 * Patterns matched against a fetched GTM container's own payload
 * (`gtm-container:` evidence blobs — see bpm-lambdas' scanService.ts).
 * Scoped deliberately to Google's own native tag types, whose internal
 * shape is consistent across every container:
 *  - `__googtag`'s domain -> measurement-ID lookup table (the "Google tag"
 *    base config; it sends a page_view automatically unless disabled)
 *  - `__awct`'s vtp_tagId (Google Ads conversion tag)
 *  - `__gaawe`'s vtp_eventName (a configured GA4 event tag)
 * Third-party pixels wrapped in GTM community/custom templates (`__cvt_*`)
 * aren't identifiable this way and are out of scope here.
 */
const CONTAINER_GA4_MAP_SRC = String.raw`"map","key","[^"]*","value","(G-[A-Z0-9]{6,12})"`;
const CONTAINER_AWCT_SRC = String.raw`"vtp_tagId":"(AW-\d{6,12})"`;
const CONTAINER_GAAWE_EVENT_SRC = String.raw`"vtp_eventName":"([^"]+)"`;
// Sentinel value gaawe uses when the event name is inherited from the
// triggering dataLayer push rather than fixed — not a real configured name.
const GAAWE_INHERITED_EVENT_NAME = 'standard';

/** All distinct capture-group-1 matches of `source` (a fresh RegExp per call — safe to reuse across blobs). */
const captureAllGroup1 = (source: string, text: string): string[] => {
  const re = new RegExp(source, 'g');
  const out = new Set<string>();
  let m = re.exec(text);
  while (m) {
    out.add(m[1]);
    m = re.exec(text);
  }
  return [...out];
};

const containerIdFromUrl = (url: string): string => url.match(/[?&]id=(GTM-[A-Z0-9]+)/)?.[1] ?? url;

/** Decode a fetched GTM container payload into container-configured fires. */
const extractContainerFires = (blob: EvidenceBlob): TagFire[] => {
  const containerId = containerIdFromUrl(blob.source.slice('gtm-container:'.length));
  const fires: TagFire[] = [];

  captureAllGroup1(CONTAINER_GA4_MAP_SRC, blob.text).forEach(id => {
    fires.push(
      makeFire(gtagPlatform(id), FireSource.CONTAINER, {
        id,
        event: 'config',
        trigger: `${containerId}: Google tag configured — sends page_view automatically unless disabled`,
        origin: blob.source,
        raw: `Google tag config in ${containerId} → measurement ID ${id}`
      })
    );
  });

  captureAllGroup1(CONTAINER_AWCT_SRC, blob.text).forEach(id => {
    fires.push(
      makeFire(gtagPlatform(id), FireSource.CONTAINER, {
        id,
        event: 'conversion',
        trigger: `${containerId}: Google Ads conversion tag configured`,
        origin: blob.source,
        raw: `Google Ads conversion tag in ${containerId} → ${id}`
      })
    );
  });

  captureAllGroup1(CONTAINER_GAAWE_EVENT_SRC, blob.text)
    .filter(name => name !== GAAWE_INHERITED_EVENT_NAME)
    .forEach(name => {
      fires.push(
        makeFire('Google Analytics 4', FireSource.CONTAINER, {
          event: name,
          trigger: `${containerId}: GA4 event tag configured`,
          origin: blob.source,
          raw: `GA4 event tag in ${containerId} → "${name}"`
        })
      );
    });

  return fires;
};

/** Decode one captured network request URL into a wire fire, if recognized. */
const wireFire = (url: string): TagFire | undefined => {
  const sig = WIRE_SIGNATURES.find(([pattern]) => pattern.test(url));
  if (!sig) return undefined;
  const platform = sig[1];
  let params: Record<string, string> = {};
  try {
    const parsed = new URL(url);
    parsed.searchParams.forEach((value, key) => {
      params[key] = value.slice(0, MAX_PARAM_VALUE);
    });
  } catch {
    params = {};
  }
  const id = WIRE_ID_PARAMS.map(k => params[k]).find(Boolean) ?? '';
  const eventRaw = WIRE_EVENT_PARAMS.map(k => params[k]).find(Boolean) ?? 'page hit';
  return makeFire(platform, FireSource.WIRE, {
    id,
    event: eventRaw,
    params,
    trigger: 'network request (runtime)',
    origin: 'network',
    raw: url
  });
};

/**
 * Extract every visible tracking call from the scan evidence (code) and,
 * when a runtime capture is available, from the observed request URLs (wire).
 */
export const extractTagFires = (
  evidenceBlobs: EvidenceBlob[],
  networkRequests: string[] = [],
  postConsentNetworkRequests: string[] = []
): TagFire[] => {
  const fires: TagFire[] = [];
  evidenceBlobs.forEach(blob => {
    if (blob.source.startsWith('gtm-container:')) {
      fires.push(...extractContainerFires(blob));
      return;
    }
    let blobCount = 0;
    CODE_EXTRACTORS.forEach(({ pattern, toFires }) => {
      const matcher = new RegExp(pattern.source, pattern.flags);
      let m = matcher.exec(blob.text);
      while (m && blobCount < MAX_FIRES_PER_BLOB) {
        const produced = toFires(m, blob.source);
        fires.push(...produced);
        blobCount += produced.length;
        m = matcher.exec(blob.text);
      }
    });
  });
  // Processed before the full networkRequests list below (which is a
  // superset including these same URLs) so that dedupeFires's first-seen-
  // wins rule tags an overlapping request as post-consent - the more useful
  // signal for an audit: it's confirmation the tag also fires once consent
  // is granted, not just evidence it existed before.
  postConsentNetworkRequests.forEach(url => {
    const fire = wireFire(url);
    if (fire) {
      fires.push({ ...fire, origin: 'network (post-consent)' });
    }
  });
  networkRequests.forEach(url => {
    const fire = wireFire(url);
    if (fire) {
      fires.push(fire);
    }
  });
  return dedupeFires(fires);
};

/** Identifies "the same call" for dedup/merge purposes. */
const fireKey = (f: TagFire): string =>
  [f.platform, f.source, f.event, f.id, Object.keys(f.params).sort().join(',')].join('|');

/** Collapse repeats of the same call (same platform/event/id/param keys). */
const dedupeFires = (fires: TagFire[]): TagFire[] => {
  const seen = new Map<string, TagFire>();
  fires.forEach(f => {
    const key = fireKey(f);
    if (!seen.has(key)) {
      seen.set(key, f);
    }
  });
  return [...seen.values()];
};

/**
 * Unions a quick scan's tagFires with a full scan's (see
 * full-scan-merge-utils.ts) rather than one replacing the other - same
 * reasoning as mergeDetections in martech-scanner-utils.ts. Each survivor
 * is tagged with which scan(s) actually produced it (`seenIn`) so the UI
 * can flag a fire the full scan never re-confirmed, or one it found that
 * the quick scan missed.
 */
export const mergeTagFires = (quickFires: TagFire[], fullFires: TagFire[]): TagFire[] => {
  const merged = new Map<string, TagFire>();
  quickFires.forEach(f => merged.set(fireKey(f), { ...f, seenIn: ['quick'] }));
  fullFires.forEach(f => {
    const key = fireKey(f);
    const existing = merged.get(key);
    if (existing) {
      existing.seenIn = existing.seenIn?.includes('full')
        ? existing.seenIn
        : [...(existing.seenIn ?? []), 'full'];
    } else {
      merged.set(key, { ...f, seenIn: ['full'] });
    }
  });
  return [...merged.values()];
};

// ── Comparison builders ─────────────────────────────────────────────────

export type MatrixCell = 'wire' | 'code' | 'container' | null;

export interface FireMatrix {
  platforms: string[];
  events: CanonicalEvent[];
  /** cells[platform][event] */
  cells: Record<string, Record<string, MatrixCell>>;
  /**
   * cells[platform][event] -> union of every contributing fire's seenIn,
   * for a scanOriginLabel() callout (see PixelIntelView.tsx) - deliberately
   * every fire that maps to the cell, not just the SOURCE_RANK winner, since
   * "was this ever confirmed by the full scan" is a different question than
   * "what's the strongest evidence type for it."
   */
  cellOrigins: Record<string, Record<string, ('quick' | 'full')[]>>;
}

const SETUP_EVENTS = new Set(['init', 'config', 'page hit']);
// gtag('consent', 'default'|'update', {...}) decodes to an event literally
// named `consent ${name}` (see CODE_EXTRACTORS below) - a prefix match
// covers every name a site passes, not just the common ones. Consent Mode's
// default call in particular fires on nearly every modern site *before* any
// user interaction, so without this a site whose only observed activity is
// setting consent state (e.g. ad_storage/analytics_storage denied by
// default) got zero matrix visibility despite the call being fully decoded.
const isSetupEvent = (event: string): boolean => {
  const lower = event.toLowerCase();
  return SETUP_EVENTS.has(lower) || lower.startsWith('consent ');
};

// Strongest evidence wins regardless of iteration order: a confirmed wire
// fire outranks a literal code call, which outranks a container-only config
// (GTM says it *would* fire this, but we didn't see a call site or a request).
const SOURCE_RANK: Record<TagFire['source'], number> = {
  [FireSource.WIRE]: 3,
  [FireSource.CODE]: 2,
  [FireSource.CONTAINER]: 1
};

/** Platforms × canonical events; strongest evidence tier wins per cell (see SOURCE_RANK). */
export const buildFireMatrix = (fires: TagFire[]): FireMatrix => {
  const cells: Record<string, Record<string, MatrixCell>> = {};
  const cellOrigins: Record<string, Record<string, Set<'quick' | 'full'>>> = {};
  fires.forEach(f => {
    if (!f.canonicalEvent) return;
    cells[f.platform] = cells[f.platform] ?? {};
    const current = cells[f.platform][f.canonicalEvent];
    if (!current || SOURCE_RANK[f.source] > SOURCE_RANK[current]) {
      cells[f.platform][f.canonicalEvent] = f.source;
    }
    cellOrigins[f.platform] = cellOrigins[f.platform] ?? {};
    cellOrigins[f.platform][f.canonicalEvent] =
      cellOrigins[f.platform][f.canonicalEvent] ?? new Set();
    const origins = cellOrigins[f.platform][f.canonicalEvent];
    (f.seenIn ?? []).forEach(source => origins.add(source));
  });
  // Platforms with only setup calls still earn a row (all-empty = telling).
  fires.forEach(f => {
    if (isSetupEvent(f.event)) {
      cells[f.platform] = cells[f.platform] ?? {};
    }
  });
  const events = CANONICAL_EVENTS.filter(e => Object.values(cells).some(row => row[e]));
  return {
    platforms: Object.keys(cells).sort(),
    events: [...events],
    cells,
    cellOrigins: Object.fromEntries(
      Object.entries(cellOrigins).map(([platform, row]) => [
        platform,
        Object.fromEntries(Object.entries(row).map(([event, set]) => [event, [...set]]))
      ])
    )
  };
};

const canonicalParams = (fire: TagFire): string[] => {
  const keys = Object.keys(fire.params).map(k => k.toLowerCase());
  return Object.entries(PARAM_CANON)
    .filter(([, natives]) => keys.some(k => natives.includes(k)))
    .map(([dim]) => dim);
};

export interface ParamCoverageRow {
  event: CanonicalEvent;
  /** platform -> canonical param dims present on that platform's fires. */
  byPlatform: Record<string, string[]>;
}

/** Which canonical parameter dimensions each platform sends per key event. */
export const buildParamCoverage = (
  fires: TagFire[],
  clientType: ClientTypeType
): ParamCoverageRow[] => {
  const keyEvents = KEY_EVENTS.filter(e => {
    const types = KEY_EVENT_TYPES[e];
    return !types || types.includes(clientType);
  });
  return keyEvents
    .map(event => {
      const byPlatform: Record<string, Set<string>> = {};
      fires
        .filter(f => f.canonicalEvent === event)
        .forEach(f => {
          byPlatform[f.platform] = byPlatform[f.platform] ?? new Set();
          canonicalParams(f).forEach(dim => byPlatform[f.platform].add(dim));
        });
      return {
        event,
        byPlatform: Object.fromEntries(
          Object.entries(byPlatform).map(([platform, dims]) => [platform, [...dims].sort()])
        )
      };
    })
    .filter(row => Object.keys(row.byPlatform).length > 0);
};

export interface FireDifference {
  severity: 'OPPORTUNITY' | 'NOTE';
  title: string;
  note: string;
}

/** Derived similar/different callouts across platforms. */
export const buildFireDifferences = (
  fires: TagFire[],
  matrix: FireMatrix,
  clientType: ClientTypeType
): FireDifference[] => {
  const out: FireDifference[] = [];

  // 1. Event-coverage gaps: an event ≥2 platforms track that another lacks.
  matrix.events.forEach(event => {
    const have = matrix.platforms.filter(p => matrix.cells[p][event]);
    const lack = matrix.platforms.filter(p => !matrix.cells[p][event]);
    if (have.length >= 2 && lack.length > 0 && event !== 'Conversion') {
      out.push({
        severity: 'OPPORTUNITY',
        title: `'${event}' is tracked by ${have.length} platform(s) but missing on ${lack.join(', ')}`,
        note:
          `${have.join(', ')} receive this signal; the others can't optimize against it. ` +
          'Aligning event coverage keeps every platform bidding on the same journey.'
      });
    }
  });

  // 2 & 3. Value/dedup completeness on this client's primary conversion
  // event(s) - Purchase for eCom, Lead for lead-gen, both independently for
  // hybrid (see PRIMARY_CONVERSION_EVENTS' doc comment for why that's fine).
  PRIMARY_CONVERSION_EVENTS[clientType].forEach(event => {
    const conversions = fires.filter(f => f.canonicalEvent === event);

    // 2. Value completeness.
    const withValue = new Set(
      conversions.filter(f => canonicalParams(f).includes('value')).map(f => f.platform)
    );
    const withoutValue = [...new Set(conversions.map(f => f.platform))].filter(
      p => !withValue.has(p)
    );
    if (withValue.size > 0 && withoutValue.length > 0) {
      out.push({
        severity: 'OPPORTUNITY',
        title: `${event} carries value on ${[...withValue].join(', ')} but not on ${withoutValue.join(', ')}`,
        note:
          `Platforms without a value on ${event} fall back to counting conversions — value-based ` +
          'bidding (tROAS, value optimization) is off for them while competitors of the signal have it.'
      });
    }

    // 3. Meta dedup readiness.
    const metaConversions = conversions.filter(f => f.platform === 'Meta (Facebook) Pixel');
    if (
      metaConversions.length > 0 &&
      !metaConversions.some(f => canonicalParams(f).includes('order id (dedup)'))
    ) {
      out.push({
        severity: 'NOTE',
        title: `Meta ${event} has no event_id — CAPI dedup not ready`,
        note:
          'Without an event_id on the browser event, adding the Conversions API later will ' +
          'double-count conversions until deduplication is wired up.'
      });
    }
  });

  // 4. Multiple destination ids on one platform.
  const idsByPlatform: Record<string, Set<string>> = {};
  fires.forEach(f => {
    if (!f.id) return;
    idsByPlatform[f.platform] = idsByPlatform[f.platform] ?? new Set();
    idsByPlatform[f.platform].add(f.id);
  });
  Object.entries(idsByPlatform)
    .filter(([, ids]) => ids.size > 1)
    .forEach(([platform, ids]) => {
      out.push({
        severity: 'NOTE',
        title: `${platform} fires to ${ids.size} different ids (${[...ids].join(', ')})`,
        note:
          'Multiple destinations usually means split ownership (site vs. agency) or a stale ' +
          'container — confirm which id is the system of record before optimizing.'
      });
    });

  // 5. Setup-only platforms.
  matrix.platforms
    .filter(p => matrix.events.every(e => !matrix.cells[p][e]))
    .forEach(platform => {
      out.push({
        severity: 'NOTE',
        title: `${platform} is installed but no journey events were seen`,
        note:
          'The base tag loads, yet no commerce/lead events were visible in this scan. Events may ' +
          'fire deeper in the funnel (checkout, forms) or via server-side — verify during onboarding.'
      });
    });

  // 6. Configured-only-via-container platforms — GTM says it would fire
  //    this, but no literal call site and no observed request confirm it.
  const byPlatform = fires.reduce<Record<string, TagFire[]>>((acc, f) => {
    (acc[f.platform] = acc[f.platform] ?? []).push(f);
    return acc;
  }, {});
  Object.entries(byPlatform)
    .filter(([, platformFires]) => platformFires.every(f => f.source === FireSource.CONTAINER))
    .forEach(([platform]) => {
      out.push({
        severity: 'NOTE',
        title: `${platform} is only configured via Tag Manager — not confirmed firing`,
        note:
          'The tag manager container is set up to fire this, but nothing in the page itself calls ' +
          "it and no live request was observed, so it's unconfirmed whether it actually reaches the " +
          'platform (consent gates, trigger conditions, or a paused tag could all block it). A ' +
          'runtime scan would confirm it on the wire.'
      });
    });

  return out;
};
